/**
 * TMS Driver Log Page — Driver activity and trip history
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCheck, Search, RefreshCcw, Loader2, ChevronRight, Truck } from 'lucide-react';
import { api } from '@/lib/api';

type JobRow = { id: string; jobNumber: string; status: string; jobType: string; driverName: string; vehicleNo: string; containerNo: string; customerName: string; pickupDate: string; deliveryDate: string; eta: string; createdAt: string };

function normalise(r: Record<string, unknown>): JobRow {
  const d = (r.data ?? {}) as Record<string, unknown>;
  return {
    id: String(r.id ?? ''),
    jobNumber: String(r.jobNumber ?? r.job_number ?? r.id ?? ''),
    status: String(r.status ?? 'draft'),
    jobType: String(r.jobType ?? r.job_type ?? ''),
    driverName: String(r.driverName ?? r.driver_name ?? d.driver_name ?? ''),
    vehicleNo: String(r.vehicleNo ?? r.vehicle_no ?? d.vehicle_no ?? ''),
    containerNo: String(r.containerNo ?? r.container_no ?? d.container_no ?? ''),
    customerName: String(r.customerName ?? r.customer_name ?? d.client_code ?? ''),
    pickupDate: String(r.pickupDate ?? r.pickup_date ?? '').slice(0, 10),
    deliveryDate: String(r.deliveryDate ?? r.delivery_date ?? '').slice(0, 10),
    eta: String(r.eta ?? '').slice(0, 10),
    createdAt: String(r.createdAt ?? r.created_at ?? '').slice(0, 10),
  };
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-sky-100 text-sky-700',
  in_transit: 'bg-yellow-100 text-yellow-700',
  delivered: 'bg-green-100 text-green-700',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-600',
};

export default function TmsDriverLogPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [driverFilter, setDriverFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo] = useState(today);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', sort: 'created_at', order: 'desc' });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get<{ items?: Record<string, unknown>[] }>(`/jobs?${params}`);
      const raw = Array.isArray(res) ? res : (res.items ?? []);
      setJobs(raw.map(normalise));
    } catch { setJobs([]); } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const drivers = useMemo(() => {
    const names = [...new Set(jobs.map((j) => j.driverName).filter(Boolean))].sort();
    return names;
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (driverFilter !== 'all' && j.driverName !== driverFilter) return false;
      const pivot = j.deliveryDate || j.pickupDate || j.eta || j.createdAt;
      if (dateFrom && pivot && pivot < dateFrom) return false;
      if (dateTo && pivot && pivot > dateTo) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![j.driverName, j.jobNumber, j.containerNo, j.customerName, j.vehicleNo].some((v) => v.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [jobs, driverFilter, dateFrom, dateTo, search]);

  const byDriver = useMemo(() => {
    const map = new Map<string, JobRow[]>();
    for (const j of filtered) {
      const key = j.driverName || '(Unassigned)';
      const list = map.get(key) ?? [];
      list.push(j);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="Driver, job, container…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={driverFilter} onValueChange={setDriverFilter}>
          <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue placeholder="All Drivers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Drivers</SelectItem>
            {drivers.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[150px] text-xs" />
        <span className="text-muted-foreground text-xs">—</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[150px] text-xs" />
        <Button variant="outline" size="sm" className="h-8" onClick={fetchJobs} disabled={loading}>
          <RefreshCcw className="h-3.5 w-3.5" />
        </Button>
        <Badge variant="secondary" className="text-xs">{filtered.length} trips</Badge>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : byDriver.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
          <UserCheck className="h-8 w-8 mx-auto mb-3 opacity-30" />
          No driver trips found for the selected date range.
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {byDriver.map(([driver, jobList]) => (
            <Card key={driver}>
              <CardContent className="pt-3 pb-3 px-4">
                <div className="flex items-center gap-2 mb-3">
                  <UserCheck className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">{driver}</span>
                  <Badge variant="secondary" className="text-[10px]">{jobList.length} job{jobList.length !== 1 ? 's' : ''}</Badge>
                  {jobList[0]?.vehicleNo && <Badge variant="outline" className="text-[10px]"><Truck className="h-3 w-3 mr-1" />{jobList[0].vehicleNo}</Badge>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Job No.</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Container</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Customer</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Pickup</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Delivery</th>
                        <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobList.map((j) => (
                        <tr key={j.id} className="border-b last:border-0 hover:bg-muted/20">
                          <td className="py-1.5 px-2">
                            <Link to={`/hms/jobs/${j.id}`} className="font-mono text-primary hover:underline flex items-center gap-0.5">
                              {j.jobNumber} <ChevronRight className="h-3 w-3" />
                            </Link>
                          </td>
                          <td className="py-1.5 px-2 font-medium">{j.jobType}</td>
                          <td className="py-1.5 px-2 font-mono">{j.containerNo || '—'}</td>
                          <td className="py-1.5 px-2 truncate max-w-[120px]">{j.customerName || '—'}</td>
                          <td className="py-1.5 px-2 text-muted-foreground">{j.pickupDate || '—'}</td>
                          <td className="py-1.5 px-2 text-muted-foreground">{j.deliveryDate || '—'}</td>
                          <td className="py-1.5 px-2">
                            <Badge className={`text-[10px] px-1.5 ${STATUS_COLORS[j.status] ?? 'bg-gray-100'}`}>{j.status.replace(/_/g, ' ')}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
